/**
 * Environment name → id resolution for every tool that takes `environment`.
 *
 * Tool descriptions have always promised "Environment name or GUID", but the
 * API layer validated the value as an ID and refused anything else:
 * `list_environments` printed "Example Production" and `list_flows` with that
 * exact string answered "Invalid environment ID format". So the listing could
 * not be used to target what it listed. Resolution happens once, at dispatch,
 * so all tools behave the same and no handler has to remember to do it.
 */
import type { FlowManagementApi } from "../api/flow-management.js";
import type { Environment } from "../api/types.js";
import { matchEnvironment } from "./switch-environment.js";

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DEFAULT_ENV = /^Default-[0-9a-f-]{36}$/i;

/** True for the two shapes the platform uses as environment ids. */
export function looksLikeEnvironmentId(value: string): boolean {
  const v = value.trim();
  return GUID.test(v) || DEFAULT_ENV.test(v);
}

const ENVIRONMENT_LIST_TTL_MS = 5 * 60 * 1000;
const cache = new WeakMap<FlowManagementApi, { at: number; environments: readonly Environment[] }>();

async function listEnvironmentsCached(api: FlowManagementApi): Promise<readonly Environment[]> {
  const hit = cache.get(api);
  if (hit && Date.now() - hit.at < ENVIRONMENT_LIST_TTL_MS) return hit.environments;
  const environments = await api.listEnvironments();
  cache.set(api, { at: Date.now(), environments });
  return environments;
}

/** Test seam: forget cached environment lists. */
export function resetEnvironmentResolutionCache(api?: FlowManagementApi): void {
  if (api) cache.delete(api);
}

/**
 * If `input.environment` is a display name rather than an id, replace it with
 * the id of the matching environment. Ids pass through untouched, so nothing
 * changes for callers that already used them; an unknown or ambiguous name is
 * refused with the list of environments the user can actually reach (the same
 * behaviour `switch_environment` has always had).
 */
export async function resolveEnvironmentInput(api: FlowManagementApi, input: unknown): Promise<unknown> {
  if (input === null || typeof input !== "object" || Array.isArray(input)) return input;
  const record = input as Record<string, unknown>;
  // Some admin tools call the parameter environmentId; the same rule applies.
  let out: Record<string, unknown> | null = null;
  for (const key of ["environment", "environmentId"] as const) {
    const requested = record[key];
    if (typeof requested !== "string" || requested.trim() === "" || looksLikeEnvironmentId(requested)) continue;
    const environments = await listEnvironmentsCached(api);
    const match = matchEnvironment(environments, requested);
    out = { ...(out ?? record), [key]: match.name };
  }
  return out ?? input;
}
